import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  #isClosing = false;
  #baseCloseDialog;

  connectedCallback() {
    super.connectedCallback();

    if (!this.#baseCloseDialog) {
      this.#baseCloseDialog = this.closeDialog;
      this.closeDialog = async () => {
        if (this.#isClosing || !this.refs.dialog.open) return;

        this.#isClosing = true;
        this.#setMobilePresentation(false);
        try {
          await this.#baseCloseDialog?.();
        } finally {
          this.#isClosing = false;
        }
      };
    }

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.refs.dialog.addEventListener('touchstart', this.#handleSurfaceTouch, { passive: false });
    this.refs.dialog.addEventListener('touchmove', this.#handleSurfaceTouch, { passive: false });
    this.refs.dialog.addEventListener('touchend', this.#handleSurfaceTouch, { passive: false });
    this.refs.dialog.addEventListener('touchcancel', this.#handleSurfaceTouch, { passive: false });
    this.refs.dialog.addEventListener('pointerdown', this.#handleSurfacePointer);
    this.refs.dialog.addEventListener('pointerup', this.#handleSurfacePointer);
    this.refs.dialog.addEventListener('pointercancel', this.#handleSurfacePointer);
    this.refs.dialog.addEventListener('click', this.#handleSurfaceClick);
    this.refs.dialog.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    this.#setMobilePresentation(false);
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.refs.dialog.removeEventListener('touchstart', this.#handleSurfaceTouch);
    this.refs.dialog.removeEventListener('touchmove', this.#handleSurfaceTouch);
    this.refs.dialog.removeEventListener('touchend', this.#handleSurfaceTouch);
    this.refs.dialog.removeEventListener('touchcancel', this.#handleSurfaceTouch);
    this.refs.dialog.removeEventListener('pointerdown', this.#handleSurfacePointer);
    this.refs.dialog.removeEventListener('pointerup', this.#handleSurfacePointer);
    this.refs.dialog.removeEventListener('pointercancel', this.#handleSurfacePointer);
    this.refs.dialog.removeEventListener('click', this.#handleSurfaceClick);
    this.refs.dialog.removeEventListener('close', this.#handleDialogClose);
  }

  #isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  #setMobilePresentation(isOpen) {
    const header = this.closest('#header-component');

    if (!this.#isMobile()) {
      this.refs.dialog.setAttribute('scroll-lock', '');
      header?.removeAttribute('data-mobile-cart-open');
      return;
    }

    // Root overflow locking breaks sticky positioning in mobile Safari.
    this.refs.dialog.removeAttribute('scroll-lock');
    document.documentElement.removeAttribute('scroll-lock');

    if (!isOpen) {
      header?.removeAttribute('data-mobile-cart-open');
      return;
    }

    header?.setAttribute('data-mobile-cart-open', '');
  }

  #handleDialogClose = () => {
    this.#setMobilePresentation(false);
    this.refs.dialog.style.removeProperty('transform');
  };

  #handleSurfaceTouch = (event) => {
    if (!this.#isMobile() || !(event.target instanceof Element)) return;
    if (
      event.target.closest(
        'button, a, input, select, textarea, summary, [role="button"], [contenteditable], [data-cart-swipe-card]'
      )
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  #handleSurfaceClick = (event) => {
    if (!this.#isMobile()) return;
    if (!(event.target instanceof Element)) return;
    const dialogRect = this.refs.dialog.getBoundingClientRect();
    if (
      event.clientX < dialogRect.left ||
      event.clientX > dialogRect.right ||
      event.clientY < dialogRect.top ||
      event.clientY > dialogRect.bottom
    ) {
      return;
    }
    if (
      event.target.closest(
        'button, a, input, select, textarea, summary, [role="button"], [contenteditable], [data-cart-swipe-card]'
      )
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  };

  #handleSurfacePointer = (event) => {
    if (!this.#isMobile() || !(event.target instanceof Element)) return;
    if (
      event.target.closest(
        'button, a, input, select, textarea, summary, [role="button"], [contenteditable], [data-cart-swipe-card]'
      )
    ) {
      return;
    }

    event.stopPropagation();
  };

  #handleCartAdd = (event) => {
    if (event.detail.data?.source === 'buy-now') return;
    if (this.hasAttribute('auto-open')) {
      // Let cart-items-component apply the fresh section markup before opening.
      requestAnimationFrame(() => this.open());
    }
  };

  async #refreshStaleCartState() {
    const renderedEmpty = this.refs.dialog.classList.contains('cart-drawer--empty');
    const cartBubble = this.querySelector('[ref="cartBubble"]');
    const cartCountIsEmpty = !(cartBubble instanceof HTMLElement) || cartBubble.classList.contains('visually-hidden');

    if (renderedEmpty === cartCountIsEmpty) return;

    const cartItems = this.querySelector('cart-items-component');
    const sectionId = cartItems instanceof HTMLElement ? cartItems.dataset.sectionId : undefined;
    if (!sectionId) return;

    try {
      await sectionRenderer.renderSection(sectionId, { cache: false });
    } catch (error) {
      console.error('Unable to refresh cart drawer state:', error);
    }
  }

  async open() {
    await this.#refreshStaleCartState();
    this.#setMobilePresentation(true);
    this.showDialog();

    if (this.#isMobile()) {
      document.documentElement.removeAttribute('scroll-lock');
    }

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
